/**
 * ============================================================
 * KEJJA OS — src/pages/settings/HouseholdPage.tsx
 * Edit home name + motto.
 * ============================================================
 */

import { useState, useEffect, type FormEvent } from 'react';
import { Save, Check, Home as HomeIcon, AlertCircle } from 'lucide-react';
import { useSettingsContext } from './SettingsLayout';
import { useMyHomes } from '@/hooks/useMe';
import { useUpdateHomeDetails } from '@/hooks/useSettings';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

export default function HouseholdPage() {
  const { homeId, myRole } = useSettingsContext();
  const { data: homesData, isLoading } = useMyHomes();
  const updateMut = useUpdateHomeDetails(homeId);

  const first = homesData?.homes?.[0];

  const [name, setName] = useState('');
  const [motto, setMotto] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (first?.home) {
      setName(first.home.name ?? '');
      setMotto(first.home.motto ?? '');
    }
  }, [first]);

  if (isLoading) return <Skeleton className="h-48" />;
  if (!first) return <div className="text-sm text-muted-foreground">No home.</div>;

  const isAdmin = myRole === 'owner' || myRole === 'parent';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;
    setSaved(false);
    updateMut.mutate({
      name: name.trim() || undefined,
      motto: motto.trim() || null,
    }, { onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); } });
  };

  return (
    <div className="space-y-4 pb-12">
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 flex items-center justify-center">
              <HomeIcon className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{first.home.name}</h3>
                <Badge variant="outline" className="text-[10px] capitalize">{myRole}</Badge>
              </div>
              {first.home.motto && (
                <p className="text-[11px] italic text-muted-foreground">"{first.home.motto}"</p>
              )}
            </div>
          </div>

          {!isAdmin && (
            <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md p-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Only admins can edit home name and motto.</span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Home name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!isAdmin}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs">Motto (optional)</Label>
              <Input
                value={motto}
                onChange={(e) => setMotto(e.target.value)}
                disabled={!isAdmin}
                placeholder='"Upendo na Umoja"'
                className="mt-1"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                A tagline for your household, shown throughout the app.
              </p>
            </div>

            {isAdmin && (
              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Button
                  type="submit"
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  disabled={updateMut.isPending}
                >
                  <Save className="w-3.5 h-3.5 mr-1" />
                  {updateMut.isPending ? 'Saving…' : 'Save'}
                </Button>
                {saved && (
                  <span className="text-xs text-emerald-600 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Saved
                  </span>
                )}
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
